import { formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import * as moment from 'moment';
import { NzModalService } from 'ng-zorro-antd/modal';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { CalendarSmartService } from 'src/app/_services/calendar-smart.service';
import { CalendarService } from 'src/app/_services/calendar.service';
import { TeamsService } from 'src/app/_services/teams.service';
import { UtilityService } from 'src/app/_services/utility.service';
import { BaseComponent } from '../base/base.component';
import { ModalDayTypeComponent } from './modal-day-type/modal-day-type.component';
import { ModalGenerateMonthComponent } from './modal-generate-month/modal-generate-month.component';
import { ModalMaxSmartComponent } from './modal-max-smart/modal-max-smart.component';
import { ModalSeatsComponent } from './modal-seats/modal-seats.component';

// GESTIRE IL CASO IN CUI VENGA SETTATO/TOLTO LO SMART DALLE CHECKBOX AGGIUNGENDO PROBABILMENTE LE REQUEST FAKE DA FE

export enum workModeEnum {
  OFFICE = 0,
  REMOTE = 1,
  REMOTEMORNING = 2,
  REMOTEAFTERNOON = 3
}
@Component({
  selector: 'app-calendar-smart',
  templateUrl: './calendar-smart.component.html',
  styleUrls: ['./calendar-smart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarSmartComponent extends BaseComponent implements OnInit {
  private readonly _titleService: UtilityService = inject(UtilityService);
  private readonly _modalService: NzModalService = inject(NzModalService);
  private readonly _calendarService: CalendarService = inject(CalendarService);
  public readonly _utilityService: UtilityService = inject(UtilityService);
  private readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly _calendarSmartService: CalendarSmartService =
    inject(CalendarSmartService);
  private readonly _teamService: TeamsService = inject(TeamsService);
  private readonly title: Title = inject(Title);

  listOfColumns: any[] = [];

  users: any[] = [];
  limit: number = 0;
  isSmartEdit: boolean = false;
  rowDaysSmart: any[] = [];
  smartOptions: any[] = [
    {
      img: 'assets/images/icon_calendar/office.svg',
      label: 'Ufficio',
      secondaryLabel: '(Default)',
      value: 14
    },
    {
      img: 'assets/images/icon_calendar/smart.svg',
      label: 'Remoto',
      value: 2
    },
    {
      img: 'assets/images/icon_calendar/morning_smart.svg',
      label: 'Remoto al mattino',
      value: 3
    },
    {
      img: 'assets/images/icon_calendar/afternoon_smart.svg',
      label: 'Remoto al pomeriggio',
      value: 4
    }
  ];
  currentDate: Date = new Date();
  activeUsers: any[] = [];
  usersSelected: any[];
  usersSelectedSubject = new Subject<any[]>();
  teamsSelected: any[];
  rolesSelected: any[];
  teams: any[] = [];
  roles: any[] = [
    { label: 'Sviluppatore', value: 1 },
    { label: 'Sistemista', value: 2 },
    { label: 'Designer', value: 3 },
    { label: 'Scrum Master', value: 4 },
    { label: 'Team Leader', value: 5 },
    { label: 'Project Manager', value: 6 }
  ];
  previousMonthExist: boolean = false;
  nextMonthExist: boolean = false;
  isDraftEvents: boolean = false;
  isDraftMonth: boolean = false;

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor() {
    super();
  }

  ngOnInit() {
    this._titleService.setTitle('Calendario Smart');
    this.title.setTitle('Calendario Smart | Sviluppo Personale');
    this.getSmartCalendarData();

    this.getActiveUsers();
    this.getTeams();

    this.usersSelectedSubject
      .pipe(distinctUntilChanged(), debounceTime(1000))
      .subscribe(() => {
        this.getSmartCalendarData();
      });
  }

  /**
   * Metodo principale che serve per inizializzare la creazione di calendario
   * @param search array per degli utenti filtrati
   * @param date data per filtraggio (opzionale)
   */
  getSmartCalendarData(date?: string): void {
    const firstMonday = this.calculateFirstMondayOfMonth(date);
    const dataStartWeek = firstMonday.format('YYYY/MM/DD');
    this.fetchCalendarData(dataStartWeek);
  }

  // Metodo privati che serve per ricavare il primo lunedì del mese
  private calculateFirstMondayOfMonth(date?: string): moment.Moment {
    const m = moment(date ?? this.currentDate).startOf('month');
    return m.isoWeekday() === 1 ? m : m.isoWeekday(8);
  }

  // Metodo che serve per recuperare i dati relativi a calendario
  private fetchCalendarData(dataStartWeek: string): void {
    this._calendarService
      .getCalendarUsers(
        dataStartWeek,
        this.usersSelected,
        this.teamsSelected,
        this.rolesSelected,
        [],
        true
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => this.handleCalendarResponse(res),
        error: () =>
          this.responseToast('error', 'Errore nel caricamento dei dati')
      });
  }

  // Metodo che, una volta recuperati i dati, va a costruire tutti gli oggetti necessari alla visualizzazione del calendario
  private handleCalendarResponse(res: any): void {
    this.processColumns(res.listOfColumns);
    this.processUsers(res.users);
    this.processMonthNavigation(res.previousAndNext);
    this.rowDaysSmart = this.createRowDaysSmart(res.listOfColumns);
    this.limit = this.listOfColumns[0].limit;
    this.isDraftEvents = res.is_draft_events;
    this.isDraftMonth = res.is_draft_month;
    this._cdr.markForCheck();
  }

  // Metodo che serve per inserire, nell'array delle colonne, la prima lettera del giorno della settimana
  private processColumns(columns: any[]): void {
    this.listOfColumns = this.createListOfColumns(columns).map(day => ({
      ...day,
      weekdayLetter: this.getFirstLetterOfWeekday(day.date)
    }));
  }

  // Metodo che serve per mappare i primi 3 progetti all'interno dell'oggetto user
  private processUsers(users: any[]): void {
    this.users = users.map((user: any) => ({
      ...user,
      firstThreeProjects: user.projects.slice(0, 3)
    }));
  }

  // Metodo che recupera se esiste l'esistenza di un mese successivo/precedente
  private processMonthNavigation(monthData: any): void {
    this.previousMonthExist = monthData.previousMonthExists;
    this.nextMonthExist = monthData.nextMonthExists;
  }

  /**
   * Questa funzione serve per creare l'array listOfColumn
   * @param response larray delle colonne che arriva da BE
   * @returns array delle colonne per calendario smart
   */
  createListOfColumns(response: any[]): any[] {
    const result: any[] = [];

    // escludiamo il primo elemento (intestazione mese)
    const days = response.slice(1);

    if (!days.length) {
      return result;
    }

    // range delle date
    const startDate = new Date(`${days[0].date}T00:00:00Z`);
    const endDate = new Date(`${days[days.length - 1].date}T00:00:00Z`);

    // mappiamo le date presenti nella response per accesso veloce
    const mapByDate = new Map(days.map(d => [d.date, d]));

    for (let d = new Date(startDate); d <= endDate; ) {
      const dateStr = d.toISOString().split('T')[0];
      const dayOfWeek = d.getUTCDay();

      const dayObj = mapByDate.get(dateStr);

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        result.push({ date: dateStr, business: true });
      } else if (dayObj) {
        result.push({
          date: dateStr,
          workers: dayObj.workers ?? 0,
          limit: dayObj.limit ?? 0
        });
      }

      // incrementa usando i metodi UTC (questo è il vero fix!)
      d.setUTCDate(d.getUTCDate() + 1);
    }

    return result;
  }

  /**
   * Questa funzione serve per creare l'array rowDaysSmart
   * @param response array delle colonne che arriva da BE
   * @returns array
   */
  createRowDaysSmart(response: any[]): any[] {
    const result: any[] = [];

    // Escludiamo il primo elemento che è intestazione del mese
    const days = response.slice(1);
    if (!days.length) return result;

    // Range tra la prima e l’ultima data — forziamo UTC
    const startDate = new Date(`${days[0].date}T00:00:00Z`);
    const endDate = new Date(`${days[days.length - 1].date}T00:00:00Z`);

    // Mappa per lookup veloce
    const mapByDate = new Map(days.map(d => [d.date, d]));

    // Verifica se ci sono utenti
    const hasUsers = this.users && this.users.length > 0;

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setUTCDate(d.getUTCDate() + 1)
    ) {
      const dateStr = d.toISOString().split('T')[0];
      const dayOfWeek = d.getUTCDay();
      const dayObj = mapByDate.get(dateStr);

      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // weekend
        result.push({
          date: dateStr,
          business: true,
          dayType: dayObj?.dayType ?? null
        });
      } else {
        // Se non ci sono utenti, il valore è sempre false
        let workMode = false;

        if (hasUsers) {
          // Altrimenti, controlliamo se tutti gli utenti in quel giorno sono in smart
          const allSmart = this.users.every((user: any) => {
            const userDay = user.days.find((ud: any) => ud.date === dateStr);
            return userDay?.event?.workMode === 2; // 2 = smart
          });
          workMode = allSmart;
        }

        result.push({
          date: dateStr,
          workMode,
          dayType: dayObj?.dayType ?? null
        });
      }
    }

    return result;
  }

  /**
   * Funzione che torna il nome del mese per intero. Es Settembre
   * @param date una qualsiasi data del mese che vogliamo recuperare
   * @returns stringa
   */
  getMonth(date: string): string {
    const currentMonth = new Date(date);
    const formatter = new Intl.DateTimeFormat('it-IT', {
      month: 'long',
      year: 'numeric'
    });
    const monthYear = formatter.format(currentMonth);
    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  }

  /**
   * Funzione che serve per recuperare l'icona da assegnare per ogni giorno, in base a limite dei posti
   * e a quanti posti sono stati occupati
   * @param workers il numero dei lavoratori
   * @param limit il numero dei posti massimi
   * @returns immagine
   */
  getImages(workers: number, limit: number): string {
    if (workers <= limit - 6)
      return 'assets/images/icon_calendar/person_green.svg';
    else if (workers > limit - 6 && workers <= limit - 1)
      return 'assets/images/icon_calendar/person_orange.svg';
    else if (workers >= limit)
      return 'assets/images/icon_calendar/person_red.svg';
    return '';
  }

  /**
   * Funzione che serve per recuperare il giorno della settimana, tornando solo la prima lettera.
   * Es: Lunedì = L
   * @param dateString data di cui vogliamo recuperare il giorno della settimana
   * @returns {string} prima lettera della settimana
   */
  getFirstLetterOfWeekday(dateString: string): string {
    const dayName = formatDate(dateString, 'EEEE', 'it-IT');
    return dayName.charAt(0).toUpperCase();
  }

  /**
   * Funzione che serve per recuperare solo i primi 3 progetti di cui un utente fa parte
   * @param user Utente
   * @returns {any[]} progetti
   */
  getFirstThreeProject(user: any): any[] {
    return user.projects.slice(0, 3);
  }

  /**
   * Funzione che serve per recuperare tutti i progetti, skippando i primi 3
   * @param user Utente
   * @returns {any[]} progetti
   */
  getOtherProject(user: any): any[] {
    return user.projects.slice(3);
  }

  /**
   * Funzione che serve per creare la modale dei posti
   */
  openSeatsModal() {
    const modalRef = this._modalService.create({
      nzContent: ModalSeatsComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '416px',
      nzClassName: 'custom-modal-seats',
      nzComponentParams: {
        limit: this.limit
      }
    });

    /**
     * alla chiusura della modale, se esiste event vengono modificati tutti i posti giornalieri
     * andando a settare i posti impostati della modale
     */
    modalRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event) {
          // vado a creare un oggetto dove vado a prendere il primo e l'ultimo giorno del mese del calendario
          // e vado a settare i posti che ci sono inseriti sulla modale
          const data = {
            start_day: this.listOfColumns[0].date,
            end_day: this.listOfColumns[this.listOfColumns.length - 1].date,
            seating: event
          };
          this._calendarSmartService
            .seatSeatsForMonth(data)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.responseToast('success', 'Posti aggiornati con successo');
                this.getSmartCalendarData();
              },
              error: () => {
                this.responseToast('error', 'Qualcosa è andato storto');
              }
            });
        }
      });
  }

  /**
   * Funzione che serve per aprire la modale per modificare un singolo giorno
   * @param date Data del giorno da modificare
   * @param limit posti massimi per quel giorno
   * @param dayType tipologia di giorno impostata
   * @param index indice del giorno da modificare
   */
  openDayTypeModal(
    date: string,
    limit: number,
    dayType: number,
    index: number
  ) {
    const modalRef = this._modalService.create({
      nzContent: ModalDayTypeComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '496px',
      nzClassName: 'custom-modal-seats',
      nzComponentParams: {
        limit: limit,
        date: new Date(date),
        dayType: dayType,
        workMode: this.rowDaysSmart[index].hybridOption
      }
    });

    /**
     * alla chiusura della modale controlla se è stata impostata una tipologia di giornata
     */
    modalRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event.regular) {
          // se abbiamo impostato giornata ordinaria
          this.setDayEvent(13, event.limit, date, index, true);
          return;
        }

        if (event.day_type) {
          switch (event.day_type) {
            case 1: // caso in cui settiamo il day off
              this.setDayEvent(event.day_type, event.limit, date, index, true);
              break;
            case 2: // caso in cui settiamo smart intera giornata
              this.setDayEvent(event.day_type, event.limit, date, index, true);
              break;
            case 14: // caso in cui settiamo presenza obbligatoria
              this.setDayEvent(event.day_type, event.limit, date, index, true);
              break;
            case 'hybrid-day': // caso in cui settiamo la giornata ibrida
              this.setDayEvent(
                event.hybridOption,
                event.limit,
                date,
                index,
                true
              );
              break;
            default:
              break;
          }
        }
      });
  }

  /**
   * Metodo che serve per impostare la tipologia di giorno
   * @param eventType Id del tipo di giorno da impostare
   * @param limit posti a sedere
   * @param date data dell'evento da impostare
   */
  setDayEvent(
    eventType: number,
    limit: number,
    date: string,
    index: number,
    isGlobal: boolean = false
  ) {
    const usersEditedIds: any[] = this.users.map((user: any) => user.id);

    this._calendarSmartService
      .setDayEvent(eventType, limit, date)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // se abbiamo settato presenza oppure day off non crea nessun evento ma richiama semplicemente la index
          if (eventType !== 13 && eventType !== 1) {
            this.manageSmartForUsers(
              date,
              usersEditedIds,
              eventType,
              index,
              isGlobal
            );
          } else {
            this.getSmartCalendarData();
          }
        },
        error: () => {
          this.responseToast('error', 'Qualcosa è andato storto');
        }
      });
  }

  /**
   *
   * @param data
   * @param userIds
   * @param eventTypeId
   * @param isGlobal vero se viene chiamato dalla modale (evento globale), false per il resto
   */
  manageSmartForUsers(
    date: string,
    userIds: string[],
    eventTypeId: number,
    indexDay: number,
    isGlobal: boolean = false
  ) {
    this._calendarSmartService
      .manageSmartForUsers({
        date,
        user_ids: userIds,
        event_type_id: eventTypeId,
        is_request: false, // sempre false perchè è un evento impostato dall'admin
        is_global: isGlobal
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (isGlobal) {
            this.getSmartCalendarData();
          } else {
            const updatedDays = res.events;
            this.users = this.users.map(user => {
              const update = updatedDays.find((u: any) => u.userId === user.id);
              if (update) {
                if (user.days[indexDay]) {
                  user.days[indexDay].event = update;
                }
              }
              return user;
            });
            this.listOfColumns[indexDay].workers = res.workers;
            this._cdr.markForCheck();
          }
          this.isDraftEvents = true;
        },
        error: () => {
          this.responseToast('error', 'Smart non impostato');
        }
      });
  }

  /**
   * Controlla se l'admin NON ha impostato
   * il target di smart working settimanale dell'utente.
   *
   * @param user utente
   * @returns true se il l'utente NON ha raggiunto lo smart desidearto, altrimenti false
   */
  isSmartWeeklyTargetMissing(user: any): boolean {
    const target = user.desired_smart_days;

    // Se non esiste, restituisce true
    if (
      target === undefined ||
      target === null ||
      target === '' ||
      Number.isNaN(Number(target)) ||
      Number(target) <= 0
    ) {
      return true;
    }

    return false;
  }

  /**
   * Funzione che serve per abilitare le modifiche
   */
  enableSmartEdit() {
    this.isSmartEdit = true;
  }

  /**
   * Funzione che serve per disabilitare le modifiche
   */
  disableSmartEdit() {
    this.isSmartEdit = false;
  }

  /**
   * Funzione che serve per settare o togliere lo smart a tutti gli utenti per quel giorno
   * @param index indice dell'array dei giorni
   * @param event booleano per capire se sta settando o togliendo lo smart
   */
  setIsDaySmart(index: number, event: any, isGlobal: boolean = false) {
    const usersEditedIds: any[] = this.users.map((user: any) => user.id);
    const date = this.users[0].days[index].date;
    this.manageSmartForUsers(
      date,
      usersEditedIds,
      event ? 2 : 14,
      index,
      isGlobal
    );
  }

  /**
   * Funzione che serve per calcolare quanto smart, un utente, ha in una settimana passando una data
   * @param user utente
   * @param targetDate data da cui ricavare la settimana (stringa ISO o Date)
   * @returns somma dei pesi (weight) degli smart day della settimana (Lun-Ven)
   */
  countWeeklySmart(user: any, targetDate: string | Date): number {
    let totalWeight = 0;
    const date = new Date(targetDate);

    // Calcolo del Lunedì (locale) della settimana contenente targetDate
    const dayOfWeek = date.getDay(); // 0 = Domenica, 1 = Lunedi, ...
    const monday = new Date(date);
    monday.setDate(date.getDate() - ((dayOfWeek + 6) % 7));
    monday.setHours(0, 0, 0, 0);

    // Venerdì (fine giornata)
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);

    if (!user?.days || !Array.isArray(user.days)) return 0;

    user.days.forEach((d: any) => {
      if (!d?.date) return;

      // Normalizziamo la data (mettiamo a mezzanotte locale per confronto)
      const dDate = new Date(d.date);
      dDate.setHours(0, 0, 0, 0);

      // Verifica se il giorno è tra Lunedì e Venerdì
      if (dDate >= monday && dDate <= friday) {
        // PRIMA MODIFICA CRUCIALE: consideriamo il peso indipendentemente da workMode
        const rawWeight = d.event?.weight ?? d.weight ?? 0; // tolleranza se weight è su event o root
        const weight =
          typeof rawWeight === 'string'
            ? Number.parseFloat(rawWeight)
            : Number(rawWeight);

        // se parse fallisce diventa NaN -> consideriamo 0
        if (!Number.isNaN(weight) && weight > 0) {
          totalWeight += weight;
        }
      }
    });

    return totalWeight;
  }

  changeMonth(value: string) {
    this.currentDate = moment(this.currentDate)
      .startOf('month')
      .add(value === 'next' ? 1 : -1, 'month')
      .toDate();

    this.getSmartCalendarData(moment(this.currentDate).format('YYYY-MM-DD'));
  }

  /**
   * Questa funzione serve per settare un singolo giorno di smart ad un singolo utente
   * @param index indice dell'array
   * @param user utente a cui dobbiamo modificare la modalità di lavoro
   * @param options la nuova modalità di lavoro assegnata (event_type_id)
   */
  setUserDay(index: number, user: any, options: number) {
    const date = user.days[index].date;
    // Chiamata API sempre eseguita
    this.manageSmartForUsers(date, [user.id], options, index);
  }

  /**
   * funzione che serve per scambiare i valori di current e last
   * @param array array che contiene tutti i valori current e last per ogni utente
   */
  changeValue(array: any[]) {
    array.forEach((change: any) => {
      const temp = change.current;
      change.current = change.last;
      change.last = temp;
    });
  }

  isSmartExceeded(user: any, maxSmartWeek: number): boolean {
    const weekMap = new Map<string, number>(); // settimana → somma pesi

    for (const day of user.days) {
      const date = new Date(day.date);

      const week = this.getWeekNumber(date);
      const year = date.getFullYear();
      const key = `${year}-${week}`;

      // ✔ Smart = qualsiasi evento con un peso > 0
      const weight = day.event?.weight ?? 0;

      if (weight > 0) {
        const updated = (weekMap.get(key) || 0) + weight;
        weekMap.set(key, updated);

        if (updated > maxSmartWeek) {
          return true; // superato limite settimanale
        }
      }
    }

    return false;
  }

  /**
   * funzione che ritorna il valore di smart, 1 se è lo smart per tutta la giornate; 0.5 se ha lo smart per mezza giornata
   * @param workMode modalità di lavoro
   * @returns numero che indica quanto smart sta facendo quel giorno
   */
  getSmartValue(workMode?: number): number {
    if (workMode === 2) return 1;
    if (workMode === 3 || workMode === 4) return 0.5;
    return 0;
  }

  /**
   * Funzine che serve a calcolare il numero della settimana a cui appartiene una certa data.
   * @param date giorno della settimana (Date)
   * @returns restituisce il numero della settimana
   */
  getWeekNumber(date: Date): number {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  /**
   * funzione che serve per controllare se un determinato giorno è stato impostato come dayOff
   * @param index indice del giorno da controllare
   * @returns booleano
   */
  checkDayOff(i: number) {
    const rowDay = this.rowDaysSmart[i];
    return rowDay?.dayType === 1;
  }

  trackByDate(day: any): string {
    return day.date; // Angular userà la data come identificatore unico
  }

  trackByUserId(user: any): number {
    return user.id; // Angular userà l'id utente come identificatore
  }

  isWeekend(dateString: string): boolean {
    // Crea oggetto Date dalla stringa (yyyy-mm-dd)
    const date = new Date(dateString);

    // getDay() restituisce:
    // 0 = Domenica, 1 = Lunedì, ..., 6 = Sabato
    const day = date.getDay();

    return day === 0 || day === 6;
  }

  /**
   * Funzione che serve per ottenere tutti gli utenti attivi
   */
  getActiveUsers() {
    this._calendarService
      .getActiveUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.activeUsers = res;
        }
      });
  }

  /**
   * Funzione per ricavare tutti i team di cui fa parte l'utente loggato
   */
  getTeams() {
    this._teamService
      .getTeams()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.teams = res.result;
        }
      });
  }

  /**
   * Funzione lanciata quando viene selezionato un utente
   * @param event
   */
  onSelectUser(event: any) {
    this.usersSelectedSubject.next(event);
    this.usersSelected = event;
  }

  /**
   * Funzione lanciata quando viene selezionato un utente
   * @param event
   */
  onSelectTeam(event: any) {
    this.teamsSelected = event;
    this.getSmartCalendarData();
  }

  /**
   * Funzione lanciata quando viene selezionato un utente
   * @param event
   */
  onSelectRole(event: any) {
    this.rolesSelected = event;
    this.getSmartCalendarData();
  }

  openModalGenerateMonth() {
    const modalRef = this._modalService.create({
      nzContent: ModalGenerateMonthComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '584px',
      nzClassName: 'custom-modal-generate-month'
    });

    /**
     *
     */
    modalRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event != undefined) {
          this.generateMonth(event);
        }
      });
  }

  /**
   * Metodo che serve per generare il mese successivo
   */
  generateMonth(value: boolean) {
    const lastDate = new Date(this.listOfColumns.at(-1).date);

    // giorno successivo
    const nextDay = new Date(lastDate);
    nextDay.setDate(nextDay.getDate() + 1);

    // se è weekend (0 = domenica, 6 = sabato) avanzare fino al lunedì
    while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
      nextDay.setDate(nextDay.getDate() + 1);
    }

    const firstDateForNextMonth = moment(nextDay).format('YYYY/MM/DD');

    this._calendarSmartService
      .generateMonth(firstDateForNextMonth, value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.responseToast('success', 'Mese generato con successo');
          this.getSmartCalendarData();
        },
        error: () => {
          this.responseToast('error', 'Qualcosa è andato storto');
        }
      });
  }

  /**
   * Questa funzione serve per aprire la modale per settare il massimo dei giorni smart settimanali
   * @param user utente di cui vogliamo settare il massimo dei giorni smart settimanali
   */
  openModalSetMaxSmart(user: any) {
    const modalRef = this._modalService.create({
      nzContent: ModalMaxSmartComponent,
      nzFooter: null,
      nzClosable: false,
      nzWidth: '424px',
      nzClassName: 'custom-modal-generate-month',
      nzComponentParams: {
        user: user,
        maxSmart: user.max_smart_days
      }
    });

    /**
     * Non appena chiudiamo la modale, se abbiamo settato i posti, andiamo a salvarli
     */
    modalRef.afterClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((smartDays: number) => {
        if (smartDays !== null && smartDays !== undefined) {
          this._calendarSmartService
            .storeMaxSmartDaysForUser(user.id, smartDays)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {
                this.responseToast('success', res.message);
                this.getSmartCalendarData();
              },
              error: () => {
                this.responseToast('error', 'Qualcosa è andato storto');
              }
            });
        }
      });
  }

  /**
   * Metodo che serve per publicare degli eventi oppure per pubblicare un nuovo calendario
   * @returns
   */
  confirmEventsOrPublishCalendar() {
    if (!this.listOfColumns.length)
      return console.error('errore nella pubblicazione degli eventi/del mese');

    // recupero i giorni necessari affinchè il BE possa eseguire i propri controlli
    const firstDay = this.listOfColumns[0].date;
    const lastDay = this.listOfColumns.at(-1).date;

    // chiamata API che serve per pubblicare tutti gli eventi
    this._calendarService
      .confirmEventsOrPublishCalendar(firstDay, lastDay)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.responseToast('success', res.message);
          this.getSmartCalendarData();
        },
        error: (err: any) => {
          this.responseToast('error', err.error.message);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
